import { api } from './api';
import { BankAccount, BankTransaction } from '../types';

export interface BankAccountFormData {
  accountName: string;
  accountNumber: string;
  bankName: string;
  accountType: string;
  balance?: number;
  chartOfAccountId?: string | null;
}

export interface BankAccountsResponse {
  accounts: BankAccount[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface BankTransactionsResponse {
  transactions: BankTransaction[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface AccountBalanceResponse {
  accountId: string;
  accountName: string;
  balance: number;
  totalIncome: number;
  totalExpense: number;
  lastUpdated: string;
}

class BankAccountService {
  private readonly endpoint = '/bank-accounts';

  async getBankAccounts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<BankAccountsResponse> {
    const endpoint = '/bank-accounts';
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.isActive !== undefined) searchParams.append('isActive', params.isActive.toString());

    const response = await api.get(`${endpoint}?${searchParams.toString()}`);
    return response.data;
  }

  async getBankAccount(id: string): Promise<BankAccount> {
    const endpoint = '/bank-accounts';
    const response = await api.get(`${endpoint}/${id}`);
    return response.data;
  }

  async createBankAccount(data: BankAccountFormData): Promise<BankAccount> {
    const endpoint = '/bank-accounts';
    console.log('🚀🆕 FRESH BUILD - Creating bank account with endpoint:', endpoint);
    console.log('🔗 Base URL:', api.defaults.baseURL);
    console.log('🔗 Full URL will be:', `${api.defaults.baseURL}${endpoint}`);
    console.log('📊 Data being sent:', data);
    console.log('⏰ Timestamp:', new Date().toISOString());
    console.log('🔍 API instance:', api);
    const response = await api.post(endpoint, data);
    console.log('✅ Bank account created successfully');
    return response.data;
  }

  async updateBankAccount(id: string, data: Partial<BankAccountFormData>): Promise<BankAccount> {
    const endpoint = '/bank-accounts';
    const response = await api.put(`${endpoint}/${id}`, data);
    return response.data;
  }

  async deleteBankAccount(id: string): Promise<{ message: string }> {
    const endpoint = '/bank-accounts';
    console.log('🗑️ Deleting bank account with endpoint:', endpoint);
    const response = await api.delete(`${endpoint}/${id}`);
    console.log('✅ Bank account deleted successfully');
    return response.data;
  }

  async getAccountBalance(id: string): Promise<AccountBalanceResponse> {
    const endpoint = '/bank-accounts';
    const response = await api.get(`${endpoint}/${id}/balance`);
    return response.data;
  }

  async getAccountTransactions(id: string, params?: {
    page?: number;
    limit?: number;
    type?: 'INCOME' | 'EXPENSE' | 'TRANSFER';
    startDate?: string;
    endDate?: string;
  }): Promise<BankTransactionsResponse> {
    const endpoint = '/bank-accounts';
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.type) searchParams.append('type', params.type);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);

    const response = await api.get(`${endpoint}/${id}/transactions?${searchParams.toString()}`);
    return response.data;
  }

  async createAdjustment(bankAccountId: string, data: {
    amount: number;
    type: 'ADD' | 'REDUCE';
    date: string;
    description?: string;
    reference?: string;
  }): Promise<any> {
    const endpoint = '/bank-accounts';
    console.log('💰 Creating bank adjustment:', data);
    const response = await api.post(`${endpoint}/${bankAccountId}/adjustment`, data);
    console.log('✅ Bank adjustment created successfully');
    return response.data;
  }

  async getReconciliationData(id: string, params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<any> {
    const endpoint = '/bank-accounts';
    const searchParams = new URLSearchParams();

    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);

    const response = await api.get(`${endpoint}/${id}/reconciliation?${searchParams.toString()}`);
    return response.data;
  }

  async reconcileTransactions(id: string, data: {
    transactionIds: string[];
    statementBalance?: number;
    reconciledDate?: string;
  }): Promise<any> {
    const endpoint = '/bank-accounts';
    const response = await api.post(`${endpoint}/${id}/reconciliation`, data);
    return response.data;
  }

  async createBankTransaction(bankAccountId: string, data: {
    description: string;
    amount: number;
    type: 'INCOME' | 'EXPENSE' | 'TRANSFER';
    date: string;
    reference?: string;
    notes?: string;
  }): Promise<any> {
    const endpoint = '/bank-accounts';
    const response = await api.post(`${endpoint}/${bankAccountId}/bank-transactions`, data);
    return response.data;
  }

  async deleteBankTransaction(bankAccountId: string, transactionId: string): Promise<any> {
    const endpoint = '/bank-accounts';
    const response = await api.delete(`${endpoint}/${bankAccountId}/bank-transactions/${transactionId}`);
    return response.data;
  }
}

const bankAccountService = new BankAccountService();
export default bankAccountService;